import { useCallback, useEffect, useRef, useState } from "react";

const WS_URL = import.meta.env.VITE_WS_URL ?? "";

export type GenerationPhase =
  | "idle"
  | "fetching_voice"
  | "researching"
  | "writing"
  | "persisting"
  | "generating"
  | "done"
  | "error";

interface WsMessage<TResult> {
  type: "progress" | "result" | "error";
  phase?: GenerationPhase;
  message?: string;
  data?: TResult;
}

interface UseGenerationPhaseReturn<TResult> {
  start: (payload?: Record<string, unknown>) => void;
  phase: GenerationPhase;
  result: TResult | null;
  error: string | null;
  reset: () => void;
}

interface UseGenerationPhaseOpts<TResult> {
  action: string;
  parseResult?: (msg: Record<string, unknown>) => TResult;
}

export function useGenerationPhase<TResult = unknown>(
  opts: UseGenerationPhaseOpts<TResult>
): UseGenerationPhaseReturn<TResult> {
  const [phase, setPhase] = useState<GenerationPhase>("idle");
  const [result, setResult] = useState<TResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const phaseRef = useRef<GenerationPhase>("idle");
  const mountedRef = useRef(true);

  const updatePhase = (p: GenerationPhase) => {
    phaseRef.current = p;
    if (mountedRef.current) setPhase(p);
  };

  // Close the socket and detach handlers on unmount so no callback fires
  // setState after the component is gone.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const ws = wsRef.current;
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close(1000);
        wsRef.current = null;
      }
    };
  }, []);

  const reset = useCallback(() => {
    updatePhase("idle");
    setResult(null);
    setError(null);
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }
  }, []);

  const start = useCallback(
    (payload?: Record<string, unknown>) => {
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
      setResult(null);
      setError(null);

      if (!WS_URL) {
        setError("VITE_WS_URL não configurado");
        updatePhase("error");
        return;
      }

      updatePhase("fetching_voice");

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ action: opts.action, ...(payload ?? {}) }));
      };

      ws.onmessage = (e: MessageEvent) => {
        if (!mountedRef.current) return;
        try {
          const msg = JSON.parse(e.data as string) as WsMessage<TResult> & Record<string, unknown>;

          if (msg.type === "progress" && msg.phase) {
            updatePhase(msg.phase);
          } else if (msg.type === "result") {
            const parsed = opts.parseResult
              ? opts.parseResult(msg as Record<string, unknown>)
              : (msg.data ?? (msg as unknown as TResult));
            setResult(parsed);
            updatePhase("done");
            ws.close(1000);
          } else if (msg.type === "error") {
            setError(msg.message ?? "Erro desconhecido");
            updatePhase("error");
            ws.close(1000);
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
          updatePhase("error");
        }
      };

      ws.onerror = () => {
        if (!mountedRef.current) return;
        setError("Erro na conexão WebSocket");
        updatePhase("error");
      };

      ws.onclose = (e: CloseEvent) => {
        if (!mountedRef.current) return;
        const currentPhase = phaseRef.current;
        if (e.code !== 1000 && currentPhase !== "done" && currentPhase !== "error") {
          setError(`Conexão fechada inesperadamente (code ${e.code})`);
          updatePhase("error");
        }
      };
    },
    [opts.action, opts.parseResult]
  );

  return { start, phase, result, error, reset };
}
